// Common color definitions for the TrackAI application

/**
 * Get a CSS variable value from the document root
 * @param variableName - CSS variable name (with or without --)
 * @param fallback - Fallback value if variable is not found
 * @returns The CSS variable value or fallback
 */
export const getCSSVariable = (variableName: string, fallback: string = ''): string => {
  // Ensure variable name starts with --
  const varName = variableName.startsWith('--') ? variableName : `--${variableName}`;
  
  // Get computed style from document root
  const rootStyles = getComputedStyle(document.documentElement);
  const value = rootStyles.getPropertyValue(varName).trim();
  
  return value || fallback;
};

export const COLORS = {
  // Section highlighting and time lost indicators
  SECTION_HIGHLIGHT: 'rgba(251, 191, 36, 0.9)', // Yellow background for section highlighting
  SECTION_HIGHLIGHT_TRANSPARENT: 'rgba(251, 191, 36, 0.1)', // Subtle yellow for clickable overlays
  
  // Other common colors
  PRIMARY_BACKGROUND: '#111827',
  SECONDARY_BACKGROUND: '#374151',
  TEXT_PRIMARY: '#f3f4f6',
  TEXT_SECONDARY: '#9ca3af',
  
  // Status colors
  SUCCESS: '#10b981',
  ERROR: '#ef4444',
  WARNING: '#f59e0b',
} as const;

/**
 * Canvas-specific colors for G-circle animations and other canvas components
 */
export const CanvasColors = {
  // Background colors (use CSS variables with fallbacks)
  get background() { return getCSSVariable('--background', '#1a1a1a'); },
  get surface() { return getCSSVariable('--surface', '#2a2a2a'); },
  
  // Text colors
  get textPrimary() { return getCSSVariable('--text-primary', '#f8fafc'); },
  get textSecondary() { return getCSSVariable('--text-secondary', '#94a3b8'); },
  
  // Grid and border colors
  grid: '#374151',
  border: '#374151',
  
  // G-circle specific colors
  gCircle: '#6b7280',
  gCircleMarkers: '#4b5563',
  
  // Data point colors
  actual: '#ef4444',
  optimal: '#10b981',
  difference: '#fbbf24',
  
  // Trail colors (with transparency)
  actualTrail: 'rgba(239, 68, 68, 0.4)',
  optimalTrail: 'rgba(16, 185, 129, 0.4)',
} as const;
